import { supplant } from "./strings.js";

/**
 * Uploads the given FormData to the given URL using XMLHttpRequest so that
 * upload progress can be reported (the fetch API can't do that).
 *
 * @param  {String} url
 *         URL to POST the form data to.
 * @param  {FormData} formData
 *         Form data to upload.
 * @param  {Function} [onProgress]
 *         Optional callback executed on each upload progress event. It's
 *         passed the ProgressEvent.
 * @return {Promise}
 *         A promise that resolves with an object containing the response
 *         `status`, `statusText` and a `text()` function, or rejects on a
 *         network error or abort.
 */
export function uploadFormData(url, formData, onProgress) {

    return new Promise((resolve, reject) => {

        const xhr = new XMLHttpRequest();
        xhr.open("POST", url);
        xhr.responseType = "text";

        if (onProgress) {
            xhr.upload.addEventListener("progress", onProgress);
        }

        xhr.addEventListener("load", () => resolve({
            status: xhr.status,
            statusText: xhr.statusText,
            text: () => Promise.resolve(xhr.responseText)
        }));
        xhr.addEventListener("error", () => reject(new Error("Request failed")));
        xhr.addEventListener("abort", () => reject(new Error("Request aborted")));
        xhr.send(formData);

    });

}

/**
 * Helper function for POSTing JSON data to the given URL.
 *
 * @param  {String} url
 *         URL to POST the data to.
 * @param  {Array|Object|String|Boolean|Number|null} data
 *         JSON data to POST.
 * @return {Promise}
 *         A promise that resolves with the response from the server, converted
 *         into JSON.
 */
export function post(url, data) {
    return fetch(url, {
        method: "POST",
        mode: "cors",
        cache: "no-cache",
        credentials: "same-origin",
        headers: {
            "Accept": "application/json",
            "Content-type": "application/json"
        },
        redirect: "follow",
        referrerPolicy: "no-referrer",
        body: JSON.stringify(data)
    }).then((response) => response.json());
}

/**
 * Helper function for GETing JSON data from the given URL.
 *
 * @param  {String} url
 *         URL to GET the data from.
 * @param  {Array|Object|String|Boolean|Number|null} data
 *         Optional data to add into the URL.
 * @return {Promise}
 *         A promise that resolves with the response from the server, converted
 *         into JSON.
 */
export function get(url, data) {
    return fetch(supplant(window.decodeURIComponent(url), data), {
        method: "GET",
        mode: "cors",
        cache: "no-cache",
        credentials: "same-origin",
        headers: {
            "Accept": "application/json",
            "Content-type": "application/json"
        },
        redirect: "follow",
        referrerPolicy: "no-referrer",
    }).then((response) => response.json());
}
